/**
 * `ETag` / `If-Match`: **o cliente guarda a versao e a devolve** (`API §4`).
 *
 * A regra que o `API §4.2` fixa e a razao de isto ser do cliente e nao da tela:
 *
 * - `If-Match` **ausente** em rota que o exige -> `428 PRECONDITION_REQUIRED`. O documento
 *   descarta em voz alta o "aceitar sem `If-Match` como last-write-wins": *"e exatamente o bug
 *   que faz um balconista desfazer o trabalho do outro sem ninguem perceber"*. Se a tela
 *   precisasse lembrar do header, uma tela esqueceria — e o modo de falha seria silencioso.
 * - `If-Match` **divergente** -> conflito **com a representacao atual no corpo**, para que o
 *   cliente resolva "sem um segundo `GET` (importante em 3G)".
 *
 * O que este arquivo NAO faz: reenviar a escrita depois do conflito. Reenviar e sobrescrever.
 * Quem decide o que fazer com o carrinho que voltou e a tela (`PWA §8.5`, a folha modal "o que
 * mudou"), e ela decide sobre um dado que ja tem em maos.
 */

/**
 * A chave de um recurso versionado.
 *
 * `'cart'` e singular de proposito: `API §6.9` da ao titular **um** carrinho por vez (sessao ou
 * `cartToken`), e nao ha rota que enderece dois. Pedido e por id.
 */
export type VersionedResourceKey = 'cart' | `order:${string}`;

export function orderVersionKey(orderId: string): VersionedResourceKey {
  return `order:${orderId}`;
}

/**
 * Onde as versoes vivem: em **memoria**, e nao em `localStorage` nem em IndexedDB.
 *
 * Nao e economia. Um `ETag` persistido sobrevive ao recarregamento e ao fechamento da aba, e
 * volta afirmando uma versao que o servidor ja passou — o que produz `409` na primeira acao do
 * usuario depois de cada abertura do app. A versao e valida enquanto a resposta que a trouxe e
 * recente; do zero, o cliente faz o `GET` e recebe o `ETag` de novo.
 */
export class ResourceVersionStore {
  readonly #etags = new Map<string, string>();

  /** Grava o `ETag` que veio na resposta. `null` (header ausente) esquece o que havia. */
  remember(key: VersionedResourceKey, etag: string | null): void {
    if (etag === null || etag === '') {
      this.#etags.delete(key);
      return;
    }
    this.#etags.set(key, etag);
  }

  /** O `ETag` a mandar em `If-Match`. `null` quando o cliente ainda nao viu o recurso. */
  current(key: VersionedResourceKey): string | null {
    return this.#etags.get(key) ?? null;
  }

  forget(key: VersionedResourceKey): void {
    this.#etags.delete(key);
  }

  /** Logout / troca de conta / kill switch. */
  clear(): void {
    this.#etags.clear();
  }
}
